import { ref } from 'vue'

import { criticalLogger } from '../../utils/logger'

export interface ErrorDetails {
  message: string
  context: string
  timestamp: string
  code?: string
  details?: string[]
}

const errorState = ref<ErrorDetails | null>(null)

export class AppError extends Error {
  constructor(
    message: string,
    public context: string,
    public code?: string,
    public details?: string[]
  ) {
    super(message)
    this.name = 'AppError'
  }
}

const getUserFriendlyMessage = (error: unknown): string => {
  if (error instanceof AppError) {
    return error.message
  }

  if (error instanceof Error) {
    // Handle common error types
    if (error.message.includes('Network Error')) {
      return 'Unable to connect to the server. Please check your internet connection.'
    }
    if (error.message.includes('timeout')) {
      return 'The request timed out. Please try again.'
    }
    return error.message
  }

  return 'An unexpected error occurred'
}

export const errorHandler = {
  setError(error: unknown, context: string): void {
    const message = getUserFriendlyMessage(error)
    const details: ErrorDetails = {
      message,
      context,
      timestamp: new Date().toISOString(),
      code: error instanceof AppError ? error.code : undefined,
      details: error instanceof AppError ? error.details : undefined
    }

    errorState.value = details

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      criticalLogger.error(`[${context}] ${message}`, error)
    }
  },

  clearError(): void {
    errorState.value = null
  },

  getError(): ErrorDetails | null {
    return errorState.value
  },

  createError(message: string, context: string, code?: string, details?: string[]): AppError {
    return new AppError(message, context, code, details)
  }
}

export const useErrorHandler = (): {
  error: typeof errorState
  setError: typeof errorHandler.setError
  clearError: typeof errorHandler.clearError
  createError: typeof errorHandler.createError
} => {
  return {
    error: errorState,
    setError: errorHandler.setError,
    clearError: errorHandler.clearError,
    createError: errorHandler.createError
  }
}
